import { useCallback, useEffect, useState } from 'react'
import { get, post } from '../lib/api'
import { Collapsible } from './ui'
import Icon from './Icon'

/**
 * The migrations screen.
 *
 * Modelled on Tracker42's, which has earned its shape over years: the things
 * you can act on stay at the top, and the history -- which only grows and which
 * nobody acts on -- folds away. Three groups, because they want three different
 * things from you.
 *
 * The one deliberate difference is the Run button. Tracker42 offers it on every
 * pending row; here it appears only on the EARLIEST pending file, because 0102
 * assuming a column 0101 adds is the ordinary case and a row of identical
 * buttons invites running them in the wrong order. The server refuses
 * out-of-order regardless -- a rule the UI enforces is not enforced.
 */

type Status = 'pending' | 'applied' | 'unrecorded' | 'drift'

interface Migration {
  name: string
  status: Status
  checksum: string
  recordedChecksum: string | null
  appliedAt: number | null
  appliedBy: string | null
  method: string | null
  statements: number
  next: boolean
}

interface State {
  migrations: Migration[]
  orphans: string[]
  environment: string
}

const LABEL: Record<Status, string> = {
  pending: 'Pending',
  applied: 'Applied',
  unrecorded: 'Applied · not recorded here',
  drift: 'Applied · file changed since',
}

export default function Migrations() {
  const [state, setState] = useState<State | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [sql, setSql] = useState<Record<string, string>>({})
  const [showing, setShowing] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setState(await get<State>('/api/admin/migrations'))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the migrations.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  /* The SQL is fetched once per file and kept: every file inlined into the list
     would be 70KB of response for a screen that shows one at a time. */
  async function toggleSql(name: string) {
    if (showing === name) { setShowing(null); return }
    setShowing(name)
    if (sql[name]) return
    try {
      const r = await get<{ sql: string }>(`/api/admin/migrations/${encodeURIComponent(name)}`)
      setSql((held) => ({ ...held, [name]: r.sql }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    }
  }

  async function act(name: string, action: 'run' | 'record') {
    setBusy(name)
    setConfirming(null)
    setDone(null)
    try {
      const r = await post<{ ran: number; migrations: Migration[]; orphans: string[] }>(
        `/api/admin/migrations/${encodeURIComponent(name)}/${action}`,
      )
      setState((held) => (held ? { ...held, migrations: r.migrations, orphans: r.orphans } : held))
      setDone(action === 'run' ? `${name} — ${r.ran} statement${r.ran === 1 ? '' : 's'} applied.` : `${name} recorded.`)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not run.')
    } finally {
      setBusy(null)
    }
  }

  if (error && !state) return <p className="error" role="alert">{error}</p>
  if (!state) return <p className="muted">Loading…</p>

  const drifted = state.migrations.filter((m) => m.status === 'drift')
  const pending = state.migrations.filter((m) => m.status === 'pending')
  // Newest first: you open the history to check what ran today, and filename
  // order files that in the middle of everything ever run.
  const settled = state.migrations.filter((m) => m.status === 'applied' || m.status === 'unrecorded').reverse()

  function Row({ m }: { m: Migration }) {
    const open = showing === m.name
    return (
      <li className={m.status === 'drift' ? 'mig-row is-drift' : 'mig-row'}>
        <div className="row mig-head">
          <code className="mig-name">{m.name}</code>
          <span className={`badge badge-${m.status}`}>{LABEL[m.status]}</span>
          <span className="spacer" />
          <button type="button" onClick={() => void toggleSql(m.name)} aria-expanded={open}>
            <Icon name="code" size={16} /> SQL
          </button>

          {m.status === 'pending' && m.next && (
            <button type="button" className="primary" disabled={busy === m.name} onClick={() => setConfirming(m.name)}>
              <Icon name="play" size={16} /> Run
            </button>
          )}
          {m.status === 'pending' && !m.next && <span className="muted small">waits its turn</span>}
          {(m.status === 'pending' || m.status === 'drift') && (
            <button type="button" disabled={busy === m.name} onClick={() => void act(m.name, 'record')}>
              <Icon name="check" size={16} /> {m.status === 'drift' ? 'Accept current' : 'Mark applied'}
            </button>
          )}
        </div>

        <div className="muted small mig-meta">
          {m.statements} statement{m.statements === 1 ? '' : 's'}
          {m.appliedAt && ` · ${new Date(m.appliedAt).toLocaleString()}`}
          {m.appliedBy && ` · ${m.appliedBy}`}
          {m.method === 'recorded' && ' · recorded without running'}
          {m.status === 'unrecorded' && ' · applied from the command line'}
        </div>

        {confirming === m.name && (
          <div className="warn mig-confirm">
            <p>
              Run <code>{m.name}</code> against <strong>{state?.environment}</strong>?
              Migrations are forward-only — if this is wrong, the fix is another migration.
            </p>
            <div className="row">
              <button type="button" className="primary" onClick={() => void act(m.name, 'run')}>Yes, run it</button>
              <button type="button" onClick={() => setConfirming(null)}>Cancel</button>
            </div>
          </div>
        )}

        {open && <pre className="mig-sql">{sql[m.name] ?? 'Loading…'}</pre>}
      </li>
    )
  }

  return (
    <>
      <h3><Icon name="database" size={18} /> Migrations — {state.environment}</h3>
      {error && <p className="error" role="alert">{error}</p>}
      {done && <p className="ok" role="status">{done}</p>}

      {drifted.length > 0 && (
        <>
          <p className="warn">
            {drifted.length} applied migration{drifted.length === 1 ? ' has' : 's have'} changed since running.
            The file on disk no longer matches what was applied — read the SQL. If the change needs applying,
            write a new migration; if it does not, <strong>Accept current</strong> re-records the checksum.
          </p>
          <ul className="list mig-list">{drifted.map((m) => <Row key={m.name} m={m} />)}</ul>
        </>
      )}

      <h4>{pending.length} pending{pending.length === 0 && <span className="muted"> — nothing waiting to run</span>}</h4>
      <ul className="list mig-list">{pending.map((m) => <Row key={m.name} m={m} />)}</ul>

      <Collapsible id="migrations.history" title="Already applied" count={settled.length}>
        <ul className="list mig-list">{settled.map((m) => <Row key={m.name} m={m} />)}</ul>
      </Collapsible>

      {state.orphans.length > 0 && (
        <p className="warn">
          Applied with no file to match: {state.orphans.map((n) => <code key={n}>{n} </code>)}
          — almost always a migration renamed after it ran, which leaves the new name pending and would
          run it a second time. Read both before doing anything.
        </p>
      )}

      <p className="muted small">
        <Icon name="history" size={14} /> <code>d1_migrations</code> is the truth here — the same table
        wrangler and <code>npm run db:migrate:*</code> write. This screen records the checksum and who ran
        it beside it.
      </p>
    </>
  )
}
